import { getBranchesById } from '@/apis/branches';
import { isTemplatePlaceholder } from '@/types';
import { MembershipFilter } from '@/types/searchFilterTypes';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { MEMBERSHIP_STATUSES_WITH_IDS } from '@/constants/filterCriteria/membership';
import { RuleProperties } from '@react-awesome-query-builder/mui';
import { FilterMapper, ValidationContext } from './filterMapperTypes';
import { extractNumericIdsFromGenericEntityArray } from './util';

export const loadBranches = async ({
  token,
  domainCode,
  branchIds,
}: {
  token: string;
  domainCode: DomainCode;
  branchIds: number[];
}) => {
  const branches = await getBranchesById(token, branchIds, domainCode);
  return { branches, isMissing: branches.length !== branchIds.length };
};

export const MembershipMapper: FilterMapper = {
  checkIsIncomplete: function ({ value }: RuleProperties): boolean {
    const isNoStatusSelected =
      (value[0].statuses?.length ?? 0) === 0 && !isTemplatePlaceholder(value[0].statuses);
    return isNoStatusSelected;
  },

  toFilter({ value }: RuleProperties): MembershipFilter {
    if (!value) throw new Error('Missing value');
    // Why an array of length 1? Thats just the way it is the way we use it
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const statuses = value[0].statuses;
    if (!statuses) throw new Error('Missing statuses from value[0]');
    const statusIds = isTemplatePlaceholder(statuses)
      ? statuses
      : extractNumericIdsFromGenericEntityArray(statuses);

    const branches = value[0].branches;
    const branchIds = isTemplatePlaceholder(branches)
      ? branches
      : branches
        ? extractNumericIdsFromGenericEntityArray(branches)
        : [];

    const notRenewing = isTemplatePlaceholder(value[0].notRenewing)
      ? value[0].notRenewing
      : (value[0].notRenewing ?? '');

    return {
      name: 'membership',
      value: {
        statusIds,
        branchIds,
        notRenewing,
      },
    };
  },

  async fromFilter(filter: MembershipFilter, { token, domainCode }: ValidationContext) {
    const { statusIds, branchIds, notRenewing } = filter.value;

    const statuses = isTemplatePlaceholder(statusIds)
      ? statusIds
      : statusIds.map((statusId) => ({
          id: statusId,
          label: MEMBERSHIP_STATUSES_WITH_IDS.find((status) => status.id === statusId)?.label,
        }));

    const { branches, isMissing } = isTemplatePlaceholder(branchIds)
      ? { branches: branchIds, isMissing: false }
      : await loadBranches({ token, branchIds, domainCode });

    return {
      field: filter.name,
      operator: 'any in',
      value: [
        {
          statuses,
          branches: isTemplatePlaceholder(branches)
            ? branches
            : branches.map((branch) => ({
                ...branch,
                label: ['ag', 'fedmps'].includes(domainCode)
                  ? branch.state + ' - ' + branch.label
                  : branch.label,
                id: branch.value,
              })),
          notRenewing: notRenewing,
          errors: isMissing ? [{ field: 'branches', message: 'Missing branches' }] : [],
        },
      ],
    };
  },
};
